import { Component, EventEmitter, Output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgentPromptComponent } from './prompts/agent-prompt.component';
import { LiveCallModelsComponent } from './models/live-call-models.component';
import { AgentToolComponent } from './tools/agent-tool.component';
import { LiveCallAuthComponent } from './auth/live-call-auth.component';

type AgentAdminSection = 'prompts' | 'models' | 'tools' | 'auth';

/**
 * Agent section of Live Call Management — a sub-tabbed shell inside the Agent tab:
 *
 *   - Prompts — the product's named system prompts (name, agent persona, prompt
 *     text, target model), managed in {@link AgentPromptComponent};
 *   - Models  — the read-only realtime voice-model catalog those prompts pick
 *     from, listed in {@link LiveCallModelsComponent};
 *   - Tools  — integrations with external APIs (method, endpoint, auth config,
 *     scoped agents), managed in {@link AgentToolComponent};
 *   - Auth   — authentication configs (base URL, token, type, universal scope),
 *     managed in {@link LiveCallAuthComponent}.
 *
 * Mirrors the parent shell's pattern: all sections stay mounted (toggled with
 * `hidden`) so switching is instant and each section's state survives. The
 * prompt count is re-emitted upward so the parent's "Agent" tab badge keeps
 * showing how many prompts the product has.
 */
@Component({
  selector: 'app-agent-admin',
  standalone: true,
  imports: [CommonModule, AgentPromptComponent, LiveCallModelsComponent, AgentToolComponent, LiveCallAuthComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- Sub-tab bar -->
    <div class="px-4 xl:px-6 pt-4">
      <div
        class="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60"
        id="agent_admin_subtabs"
      >
        <button
          type="button"
          (click)="setSection('prompts')"
          class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          [ngClass]="activeSection() === 'prompts'
            ? 'bg-white dark:bg-surface-2 text-purple-600 dark:text-purple-400 shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
          id="agent_admin_subtab_prompts"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          Prompt templates
          <span
            *ngIf="promptCount() !== null"
            class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 font-bold"
          >
            {{ promptCount() }}
          </span>
        </button>

        <button
          type="button"
          (click)="setSection('models')"
          class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          [ngClass]="activeSection() === 'models'
            ? 'bg-white dark:bg-surface-2 text-purple-600 dark:text-purple-400 shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
          id="agent_admin_subtab_models"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="4" y="4" width="16" height="16" rx="2"></rect>
            <path d="M9 9h6v6H9z"></path>
            <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3"></path>
          </svg>
          AI models
          <span
            *ngIf="modelCount() !== null"
            class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 font-bold"
          >
            {{ modelCount() }}
          </span>
        </button>

        <button
          type="button"
          (click)="setSection('tools')"
          class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          [ngClass]="activeSection() === 'tools'
            ? 'bg-white dark:bg-surface-2 text-purple-600 dark:text-purple-400 shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
          id="agent_admin_subtab_tools"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
          </svg>
          Function tools
          <span
            *ngIf="toolCount() !== null"
            class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 font-bold"
          >
            {{ toolCount() }}
          </span>
        </button>

        <button
          type="button"
          (click)="setSection('auth')"
          class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          [ngClass]="activeSection() === 'auth'
            ? 'bg-white dark:bg-surface-2 text-purple-600 dark:text-purple-400 shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
          id="agent_admin_subtab_auth"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
          </svg>
          Access &amp; keys
          <span
            *ngIf="authCount() !== null"
            class="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 font-bold"
          >
            {{ authCount() }}
          </span>
        </button>
      </div>
    </div>

    <!-- Section bodies (all mounted; toggled so state and instant switching survive) -->
    <div [hidden]="activeSection() !== 'prompts'">
      <app-agent-prompt (countChange)="onPromptCount($event)"></app-agent-prompt>
    </div>
    <div [hidden]="activeSection() !== 'models'">
      <app-live-call-models (countChange)="modelCount.set($event)"></app-live-call-models>
    </div>
    <div [hidden]="activeSection() !== 'tools'">
      <app-agent-tool (countChange)="toolCount.set($event)"></app-agent-tool>
    </div>
    <div [hidden]="activeSection() !== 'auth'">
      <app-live-call-auth (countChange)="authCount.set($event)"></app-live-call-auth>
    </div>
  `,
})
export class AgentAdminComponent {
  /** Re-emits the prompt count so the parent's Agent tab badge stays accurate. */
  @Output() countChange = new EventEmitter<number>();

  activeSection = signal<AgentAdminSection>('prompts');

  /** Counts surfaced by each section, shown as badges on the sub-tabs. */
  promptCount = signal<number | null>(null);
  modelCount = signal<number | null>(null);
  toolCount = signal<number | null>(null);
  authCount = signal<number | null>(null);

  setSection(section: AgentAdminSection): void {
    this.activeSection.set(section);
  }

  onPromptCount(count: number): void {
    this.promptCount.set(count);
    this.countChange.emit(count);
  }
}
